import {
  createContext,
  createElement,
  useContext,
  type ReactNode,
} from "react";
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { QueryClient } from "@tanstack/react-query";

export interface RefreshResult {
  /** New access token. The instance will replay the failed request with this. */
  accessToken: string;
}

export interface CreateApiClientOptions {
  baseURL: string;
  getAuthToken: () => string | null;
  getTenantId: () => string | null;
  getActAsTenantId?: () => string | null;
  /**
   * Story 009 — silent refresh on 401. The api-client doesn't depend on
   * `@aeriosiq/auth-client` (would create a cycle). Apps inject a refresh callback that
   * (a) calls Zitadel's /oidc/v1/token, (b) writes rotated tokens into the
   * auth store, and (c) returns the new accessToken so the interceptor can
   * replay the original request with it. Returning `null` means "refresh
   * failed; let the 401 bubble".
   */
  refreshAuthToken?: () => Promise<RefreshResult | null>;
}

export interface AeroApiError extends Error {
  /**
   * A correlatable identifier for the failure, ALWAYS a real value (never an
   * empty string). Resolution order matches the lyse/v1 error envelopes
   * (`#/components/schemas/ApiError4xx|500`):
   *   1. `errorId` — present on the 500 `internal_error` envelope (pino log id).
   *   2. `requestId` — a server request-correlation id when the envelope ships
   *      one (defence-in-depth; some gateways add it).
   *   3. `code` — present on EVERY 4xx envelope (`not_found`, `forbidden`,
   *      `validation_error`, `conflict`, …). This is what makes a 404 surface
   *      `not_found` instead of the old `"unknown"`.
   *   4. `"unknown"` — only when none of the above exist (e.g. a network error
   *      with no response body).
   */
  errorId: string;
  /** The BE envelope `code` when present (e.g. `not_found`), else undefined. */
  code?: string;
  status?: number;
  cause?: AxiosError;
}

interface ErrorBody {
  errorId?: string;
  requestId?: string;
  code?: string;
  message?: string;
}

// Marker on InternalAxiosRequestConfig so we never refresh-replay the same
// request twice in a row (avoids tight infinite loops).
interface AeroAxiosRequestConfig extends InternalAxiosRequestConfig {
  _aeroRetried?: boolean;
}

export function createApiClient(opts: CreateApiClientOptions): AxiosInstance {
  const instance = axios.create({ baseURL: opts.baseURL });

  instance.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
    const token = opts.getAuthToken();
    const tenantId = opts.getTenantId();
    const actAs = opts.getActAsTenantId?.();

    if (token) cfg.headers.set("Authorization", `Bearer ${token}`);
    if (tenantId) cfg.headers.set("X-Tenant-ID", tenantId);
    if (actAs) cfg.headers.set("X-Act-As-Tenant", actAs);
    return cfg;
  });

  // Single in-flight refresh promise — parallel 401s queue behind the same
  // exchange and replay together once the new token arrives. Avoids hammering
  // Zitadel with N parallel refreshes.
  let refreshInFlight: Promise<RefreshResult | null> | null = null;

  instance.interceptors.response.use(
    (res) => res,
    async (err: unknown) => {
      if (
        opts.refreshAuthToken &&
        err instanceof AxiosError &&
        err.response?.status === 401 &&
        err.config
      ) {
        const cfg = err.config as AeroAxiosRequestConfig;
        if (!cfg._aeroRetried) {
          refreshInFlight ??= opts.refreshAuthToken().finally(() => {
            // Reset the slot once the in-flight refresh resolves so the
            // NEXT 401 (much later) starts a fresh exchange.
            queueMicrotask(() => {
              refreshInFlight = null;
            });
          });
          try {
            const result = await refreshInFlight;
            if (result) {
              cfg._aeroRetried = true;
              cfg.headers.set("Authorization", `Bearer ${result.accessToken}`);
              return await instance.request(cfg);
            }
          } catch {
            // fall through to the normalised error reject
          }
        }
      }
      return Promise.reject(toAeroApiError(err));
    },
  );

  return instance;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error: unknown) => {
          const status =
            error instanceof AxiosError ? error.response?.status : undefined;
          if (typeof status === "number" && status >= 400 && status < 500)
            return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

function toAeroApiError(err: unknown): AeroApiError {
  if (err instanceof AxiosError) {
    const body = err.response?.data as ErrorBody | undefined;
    // ANA-R1-01 (AC3) — resolve a REAL correlatable id even when the envelope
    // carries no `errorId` (only the 500 `internal_error` shape has one). 4xx
    // envelopes (incl. 404 `not_found`) carry `code`, so fall through to it
    // before giving up — the delete-404 toast then shows `not_found` rather
    // than the useless `"unknown"`.
    const errorId = body?.errorId ?? body?.requestId ?? body?.code ?? "unknown";
    const message = body?.message ?? err.message;
    const wrapped = new Error(message) as AeroApiError;
    wrapped.name = "AeroApiError";
    wrapped.errorId = errorId;
    if (body?.code !== undefined) wrapped.code = body.code;
    const status = err.response?.status;
    if (typeof status === "number") wrapped.status = status;
    wrapped.cause = err;
    return wrapped;
  }
  const fallback = new Error(
    err instanceof Error ? err.message : "Unknown error",
  ) as AeroApiError;
  fallback.name = "AeroApiError";
  fallback.errorId = "unknown";
  return fallback;
}

export type { AxiosRequestConfig };

// ---------------------------------------------------------------------------
// useApi() context — apps/web sets the configured axios instance at the root;
// modules (apps/lyse, future apps/route-planner, …) consume it via useApi()
// inside their query hooks. Decoupling the queries from a per-app api/client.ts
// is what lets @aeriosiq/lyse stay a library.
// ---------------------------------------------------------------------------

const ApiContext = createContext<AxiosInstance | null>(null);

export interface ApiProviderProps {
  client: AxiosInstance;
  children: ReactNode;
}

/**
 * Wraps a subtree with a configured axios instance. Mount once at the
 * umbrella root (apps/web). Modules read it via {@link useApi}.
 */
export function ApiProvider(props: ApiProviderProps) {
  return createElement(
    ApiContext.Provider,
    { value: props.client },
    props.children,
  );
}

/**
 * Retrieve the host-provided axios instance. Throws if no {@link ApiProvider}
 * is mounted above — this is a programmer error, not a runtime condition.
 */
export function useApi(): AxiosInstance {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApi() must be used within <ApiProvider>");
  }
  return client;
}

/**
 * The host-provided axios instance, or `null` where no {@link ApiProvider} is
 * mounted. For a shell-level host that only needs the client LATER (DL-02's
 * download manager renews links during a run) and must mount in a chrome
 * test that has no provider — never for a query hook, which should throw.
 */
export function useApiOptional(): AxiosInstance | null {
  return useContext(ApiContext);
}
